import { ResultData } from "@nt-portal/common-sdk";
import { AppUser, BaseDBEntity, NotFoundError } from "@nt-portal/server-sdk";
import { BaseClientService } from "../base-client.service";
import { getVerifiedCredentialsBaseUrlCandidates } from "./verified-credentials-base-url";

type QueryValue = string | number | boolean | null | undefined;

const toIsoDate = (value: unknown): string | Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return value;
  }

  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const normalizeArrayResponse = <T>(response: unknown, mapper: (item: unknown) => T): T[] => {
  if (Array.isArray(response)) {
    return response.map(mapper);
  }

  const data = (response as { data?: unknown })?.data;
  if (Array.isArray(data)) {
    return data.map(mapper);
  }

  return [];
};

export type VerifiedCredentialFieldType = "text" | "number" | "date" | "email" | "select" | "boolean";

export interface VerifiedCredentialFieldDefinition {
  key: string;
  label: string;
  type: VerifiedCredentialFieldType;
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface VerifiedCredentialDefinition extends BaseDBEntity {
  name: string;
  description?: string | null;
  fields: VerifiedCredentialFieldDefinition[];
  active?: boolean;
}

export interface VerifiedCredentialRegistration extends BaseDBEntity {
  user?: AppUser;
  definition?: VerifiedCredentialDefinition;
  data?: Record<string, unknown>;
}

export interface VerifiedCredentialRegistrationRequest {
  definitionId: string;
  data: Record<string, unknown>;
  userId?: string;
  email?: string;
}

export interface VulindlelaTokenRequest {
  persalNumber: string;
  redirectUrl?: string;
  expiresInMinutes?: number;
}

export interface VulindlelaTokenResponse {
  token: string;
}

export type WalletVerificationStatus = "pending" | "verified" | "failed" | "expired";

export interface WalletRequestedCredential {
  type: string;
  format: string;
}

export interface WalletVerifyStartRequest {
  preset?: string;
  requestedCredentials?: WalletRequestedCredential[];
}

export interface WalletVerifyStartResponse {
  state: string;
  oid4vpUrl: string;
  expiresAt: string | Date;
}

export interface WalletVerifyStatusResponse {
  state: string;
  status: WalletVerificationStatus;
  sessionId?: string;
  verifiedClaims?: Record<string, unknown>;
}

export interface WalletVerifyCredentialsResponse {
  state: string;
  sessionId?: string;
  status: WalletVerificationStatus;
  verifiedClaims?: Record<string, unknown>;
  presentedCredentials?: unknown;
}

export interface VerifiedCredentialRegistrationFilter {
  userId?: string;
  email?: string;
}

const normalizeField = (raw: any): VerifiedCredentialFieldDefinition => ({
  key: String(raw?.key ?? ""),
  label: String(raw?.label ?? ""),
  type: (raw?.type ?? "text") as VerifiedCredentialFieldType,
  required: raw?.required !== undefined ? Boolean(raw.required) : undefined,
  options: Array.isArray(raw?.options) ? raw.options.map((item: unknown) => String(item)) : undefined,
  placeholder: raw?.placeholder != null ? String(raw.placeholder) : undefined,
});

const normalizeDefinition = (raw: any): VerifiedCredentialDefinition => ({
  ...(raw && typeof raw === "object" ? raw : {}),
  id: raw?.id ? String(raw.id) : undefined,
  name: String(raw?.name ?? ""),
  description: raw?.description != null ? String(raw.description) : undefined,
  fields: Array.isArray(raw?.fields) ? raw.fields.map(normalizeField) : [],
  active: raw?.active !== undefined ? Boolean(raw.active) : true,
});

const normalizeRegistration = (raw: any): VerifiedCredentialRegistration => ({
  ...(raw && typeof raw === "object" ? raw : {}),
  id: raw?.id ? String(raw.id) : undefined,
  createdDate: toIsoDate(raw?.createdDate),
  updatedDate: toIsoDate(raw?.updatedDate),
  deletedDate: toIsoDate(raw?.deletedDate),
  user: raw?.user ? { ...raw.user } : undefined,
  definition: raw?.definition ? normalizeDefinition(raw.definition) : undefined,
  data: raw?.data && typeof raw.data === "object" ? { ...raw.data } : {},
});

export class ClientVerifiedCredentialsService extends BaseClientService {
  private readonly resourcePrefixes = ["verified-credentials"];
  private readonly baseUrls: string[];

  constructor() {
    super();
    this.baseUrls = getVerifiedCredentialsBaseUrlCandidates(
      String(this.settings.vcServer ?? ""),
      String(this.settings.fabrixServer ?? "")
    );
  }

  private buildUrl(path: string, query?: Record<string, QueryValue>, baseOverride?: string): string {
    const base = (baseOverride || this.baseUrls[0] || "/api/vcs").replace(/\/+$/, "");
    const resourcePath = path.replace(/^\/+/, "");
    const url = `${base}/${resourcePath}`;

    if (!query) {
      return url;
    }

    const queryString = Object.entries(query)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join("&");

    return queryString ? `${url}?${queryString}` : url;
  }

  private buildCredentialResourceUrl(prefix: string, path: string, query?: Record<string, QueryValue>, baseOverride?: string): string {
    const resourcePath = path.replace(/^\/+/, "");
    return this.buildUrl(`${prefix}/${resourcePath}`, query, baseOverride);
  }

  private isSameOriginBase(base: string): boolean {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      const origin = new URL(base, window.location.origin).origin;
      return origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private getRequestOptions(base: string): { withCredentials: boolean } {
    return { withCredentials: base.startsWith("/") || this.isSameOriginBase(base) };
  }

  private isNotFoundError(error: unknown): boolean {
    if (error instanceof NotFoundError) {
      return true;
    }

    const message = String((error as { message?: string })?.message ?? "");
    return /\b404\b/i.test(message) || /Cannot\s+(GET|POST|PUT|DELETE)/i.test(message);
  }

  private async withCredentialPrefixFallback<T>(
    request: (url: string, requestOptions: { withCredentials: boolean }) => Promise<T>,
    path: string | string[],
    query?: Record<string, QueryValue>
  ): Promise<T> {
    const paths = Array.isArray(path) ? path : [path];
    let lastError: unknown;
    const attemptedUrls: string[] = [];

    for (let baseIndex = 0; baseIndex < this.baseUrls.length; baseIndex++) {
      const base = this.baseUrls[baseIndex];
      const requestOptions = this.getRequestOptions(base);

      for (let pathIndex = 0; pathIndex < paths.length; pathIndex++) {
        const resourcePath = paths[pathIndex];

        for (let prefixIndex = 0; prefixIndex < this.resourcePrefixes.length; prefixIndex++) {
          const prefix = this.resourcePrefixes[prefixIndex];
          const url = this.buildCredentialResourceUrl(prefix, resourcePath, query, base);
          attemptedUrls.push(url);

          try {
            return await request(url, requestOptions);
          } catch (error) {
            lastError = error;
            const lastPrefix = prefixIndex === this.resourcePrefixes.length - 1;
            const lastPath = pathIndex === paths.length - 1;
            const lastBase = baseIndex === this.baseUrls.length - 1;

            if ((lastPrefix && lastPath && lastBase) || !this.isNotFoundError(error)) {
              throw error;
            }
          }
        }
      }
    }

    if (this.isNotFoundError(lastError)) {
      const message = String((lastError as { message?: string })?.message ?? "VC API route not found");
      throw new NotFoundError(`${message} | Tried URLs: ${attemptedUrls.join(" | ")}`);
    }

    throw lastError;
  }

  public async getDefinitions(): Promise<ResultData<VerifiedCredentialDefinition>> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.get(url, this.token, requestOptions),
      ["definitions", "definition"],
      { limit: -1 }
    );

    if (Array.isArray((response as { data?: unknown })?.data)) {
      return {
        ...(response as Record<string, unknown>),
        data: normalizeArrayResponse(response, normalizeDefinition),
      } as ResultData<VerifiedCredentialDefinition>;
    }

    return {
      data: normalizeArrayResponse(response, normalizeDefinition),
    } as ResultData<VerifiedCredentialDefinition>;
  }

  public async getDefinition(id: string): Promise<VerifiedCredentialDefinition> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.get(url, this.token, requestOptions),
      [`definitions/${encodeURIComponent(id)}`, `definition/${encodeURIComponent(id)}`]
    );

    return normalizeDefinition(response);
  }

  public async createDefinition(item: VerifiedCredentialDefinition): Promise<VerifiedCredentialDefinition> {
    const payload = {
      ...item,
      fields: (item.fields ?? []).map(normalizeField),
    };

    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.post(url, this.token, payload, requestOptions),
      ["definitions", "definition"]
    );

    return normalizeDefinition(response ?? payload);
  }

  public async updateDefinition(item: VerifiedCredentialDefinition): Promise<VerifiedCredentialDefinition> {
    const id = item.id;
    if (!id) {
      throw new Error("Definition id is required.");
    }

    const payload = {
      ...item,
      fields: (item.fields ?? []).map(normalizeField),
    };

    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.put(url, this.token, payload, requestOptions),
      [`definitions/${encodeURIComponent(id)}`, `definition/${encodeURIComponent(id)}`]
    );

    return normalizeDefinition(response ?? payload);
  }

  public async deleteDefinition(id: string): Promise<VerifiedCredentialDefinition[]> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.delete(url, this.token, requestOptions),
      [`definitions/${encodeURIComponent(id)}`, `definition/${encodeURIComponent(id)}`]
    );

    return normalizeArrayResponse(response, normalizeDefinition);
  }

  public async getRegistrations(filter?: VerifiedCredentialRegistrationFilter): Promise<VerifiedCredentialRegistration[]> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.get(url, this.token, requestOptions),
      ["registrations", "registration"],
      {
        userId: filter?.userId,
        email: filter?.email,
      }
    );

    return normalizeArrayResponse(response, normalizeRegistration);
  }

  public async getRegistration(id: string): Promise<VerifiedCredentialRegistration> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.get(url, this.token, requestOptions),
      [`registrations/${encodeURIComponent(id)}`, `registration/${encodeURIComponent(id)}`]
    );

    return normalizeRegistration(response);
  }

  public async registerCredential(payload: VerifiedCredentialRegistrationRequest): Promise<VerifiedCredentialRegistration> {
    const body = {
      definitionId: payload.definitionId,
      data: { ...payload.data },
      userId: payload.userId,
      email: payload.email,
    };

    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.post(url, this.token, body, requestOptions),
      ["registrations", "registration"]
    );

    return normalizeRegistration(response ?? { data: body.data });
  }

  public async updateRegistration(id: string, data: Record<string, unknown>): Promise<VerifiedCredentialRegistration> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.put(url, this.token, { data: { ...data } }, requestOptions),
      [`registrations/${encodeURIComponent(id)}`, `registration/${encodeURIComponent(id)}`]
    );

    return normalizeRegistration(response ?? { id, data });
  }

  public async deleteRegistration(id: string): Promise<VerifiedCredentialRegistration[]> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.delete(url, this.token, requestOptions),
      [`registrations/${encodeURIComponent(id)}`, `registration/${encodeURIComponent(id)}`]
    );

    return normalizeArrayResponse(response, normalizeRegistration);
  }

  public async requestVulindlelaToken(
    persalNumber: string,
    options?: Pick<VulindlelaTokenRequest, "redirectUrl" | "expiresInMinutes">
  ): Promise<VulindlelaTokenResponse> {
    const body: VulindlelaTokenRequest = {
      persalNumber: persalNumber?.trim(),
      redirectUrl: options?.redirectUrl,
    };

    if (typeof options?.expiresInMinutes === "number" && Number.isFinite(options.expiresInMinutes)) {
      body.expiresInMinutes = Math.trunc(options.expiresInMinutes);
    }

    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.post(url, this.token, body, requestOptions),
      ["vulindlela/token", "token/vulindlela", "vulindlela"]
    );

    return {
      token: String((response as { token?: unknown })?.token ?? ""),
    };
  }

  public async startWalletVerification(payload?: WalletVerifyStartRequest): Promise<WalletVerifyStartResponse> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.post(url, this.token, payload ?? { preset: "proof_of_residence" }, requestOptions),
      ["wallet/verify/start", "verify/start", "wallet/start"]
    );

    return {
      state: String((response as { state?: unknown })?.state ?? ""),
      oid4vpUrl: String((response as { oid4vpUrl?: unknown })?.oid4vpUrl ?? ""),
      expiresAt: toIsoDate((response as { expiresAt?: unknown })?.expiresAt) ?? "",
    };
  }

  public async getWalletVerificationStatus(state: string): Promise<WalletVerifyStatusResponse> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.get(url, this.token, requestOptions),
      [
        `wallet/verify/status/${encodeURIComponent(state)}`,
        `verify/status/${encodeURIComponent(state)}`,
      ]
    );

    return {
      state: String((response as { state?: unknown })?.state ?? state),
      status: ((response as { status?: unknown })?.status ?? "failed") as WalletVerificationStatus,
      sessionId: (response as { sessionId?: unknown })?.sessionId ? String((response as { sessionId?: unknown }).sessionId) : undefined,
      verifiedClaims: (response as { verifiedClaims?: unknown })?.verifiedClaims && typeof (response as { verifiedClaims?: unknown }).verifiedClaims === "object"
        ? { ...((response as { verifiedClaims: Record<string, unknown> }).verifiedClaims) }
        : undefined,
    };
  }

  public async getWalletVerificationCredentials(sessionId: string): Promise<WalletVerifyCredentialsResponse> {
    const response = await this.withCredentialPrefixFallback(
      (url, requestOptions) => this.apiService.get(url, this.token, requestOptions),
      [
        `wallet/verify/credentials/${encodeURIComponent(sessionId)}`,
        `verify/credentials/${encodeURIComponent(sessionId)}`,
      ]
    );

    const responseRecord = (response && typeof response === "object" ? response : {}) as Record<string, unknown>;
    return {
      state: String(responseRecord.state ?? ""),
      sessionId: responseRecord.sessionId ? String(responseRecord.sessionId) : sessionId,
      status: (responseRecord.status ?? (responseRecord.verifiedClaims || responseRecord.presentedCredentials ? "verified" : "pending")) as WalletVerificationStatus,
      verifiedClaims: responseRecord.verifiedClaims && typeof responseRecord.verifiedClaims === "object"
        ? { ...(responseRecord.verifiedClaims as Record<string, unknown>) }
        : undefined,
      presentedCredentials: responseRecord.presentedCredentials,
    };
  }
}
